import { FormEvent, useEffect, useState } from "react";
import Navbar from "../../../components/Navbar";
import API from "../../../services/api";
const USERS_PER_PAGE = 20;

interface User {
  _id: string;
  full_name?: string;
  phone_number?: string;
  email?: string;
  wallet_balance?: number;
  has_membership?: boolean;
  has_active_membership?: boolean;
}

interface CartItem {
  id: string;
  basket_id: string;
  delivery_entry_id?: string | null;
  product_id: string;
  product_name: string;
  price: number;
  mrp_price?: number;
  quantity: number;
  total_price: number;
  type: "cart" | "subscription";
  sub_type?: string | null;
  status?: string;
  is_order_placed?: boolean;
  order_id?: string | null;
  image?: string | null;
}

interface DeliveryGroup {
  delivery_date: string;
  orders_count?: number;
  subtotal: number;
  items: CartItem[];
}

interface Subscription {
  subscription_id: string;
  product_id: string;
  product_name: string;
  sub_type: string;
  quantity: number;
  price: number;
  remaining_deliveries_count: number;
  image?: string | null;
}

interface UserCartData {
  user: User & { has_membership?: boolean };
  tomorrow: DeliveryGroup & {
    is_cutoff_passed?: boolean;
    has_placed_orders?: boolean;
    delivery_charge?: number;
    delivery_charge_reason?: string;
    total_amount?: number;
  };
  rest_of_month: DeliveryGroup[];
  subscriptions: Subscription[];
}

type DeleteTarget =
  | { kind: "cart"; id: string; label: string }
  | { kind: "delivery"; id: string; label: string }
  | { kind: "subscription"; id: string; label: string };

export default function UpdateUserCartItem() {
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [cart, setCart] = useState<UserCartData | null>(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartError, setCartError] = useState("");
  const [deletingKey, setDeletingKey] = useState("");

  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError("");

      const response = await API.get("/users", {
        params: {
          page: userPage,
          limit: USERS_PER_PAGE,
          ...(search ? { search } : {}),
        },
      });

      const data = response.data?.data || response.data;
      const list: User[] = Array.isArray(data?.users) ? data.users : [];
      const pagination = data?.pagination || {};

      setUsers(list);
      setUserTotal(pagination.totalItems || list.length);
      setUserTotalPages(Math.max(1, pagination.totalPages || 1));
    } catch (error: any) {
      setUsers([]);
      setUsersError(
        error?.response?.data?.message || "Failed to load users"
      );
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPage, search]);

  const fetchUserCart = async (user: User) => {
    try {
      setSelectedUser(user);
      setCart(null);
      setCartLoading(true);
      setCartError("");

      const response = await API.get(`/user/${user._id}/cart`);
      setCart(response.data?.data);
    } catch (error: any) {
      setCartError(
        error?.response?.data?.message || "Failed to load user cart"
      );
    } finally {
      setCartLoading(false);
    }
  };

  const refreshCart = async () => {
    if (selectedUser) {
      await fetchUserCart(selectedUser);
    }
  };

  const deleteCartEntry = async (target: DeleteTarget) => {
    if (!selectedUser) return;

    const messages = {
      cart: `Remove "${target.label}" from this user's cart?`,
      delivery: `Remove the "${target.label}" subscription delivery for this date?`,
      subscription: `Cancel the entire "${target.label}" subscription?`,
    };

    if (!window.confirm(messages[target.kind])) {
      return;
    }

    const path =
      target.kind === "cart"
        ? `items/${target.id}`
        : target.kind === "delivery"
        ? `delivery/${target.id}`
        : `subscriptions/${target.id}`;

    try {
      setDeletingKey(`${target.kind}:${target.id}`);
      setCartError("");

      const response = await API.delete(
        `/user/${selectedUser._id}/cart/${path}`
      );

      alert(response.data?.message || "Item removed successfully");
      await refreshCart();
    } catch (error: any) {
      setCartError(
        error?.response?.data?.message || "Failed to remove item"
      );
    } finally {
      setDeletingKey("");
    }
  };

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    setUserPage(1);
    setSearch(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setUserPage(1);
  };

  const renderItem = (item: CartItem, deliveryDate: string) => {
    const isCartItem = item.type === "cart";
    const deleteId = isCartItem ? item.basket_id : item.delivery_entry_id;
    const deleteKey = `${isCartItem ? "cart" : "delivery"}:${deleteId}`;

    return (
      <div
        key={`${deliveryDate}-${item.id}`}
        className="flex flex-col gap-3 rounded-xl border border-gray-200 p-3 sm:flex-row sm:items-center"
      >
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-100">
          {item.image ? (
            <img
              src={item.image}
              alt={item.product_name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-2xl">
              🌸
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="font-semibold text-gray-900">
            {item.product_name}
          </div>
          <div className="mt-1 text-sm text-gray-500">
            {item.type === "subscription"
              ? `Subscription • ${item.sub_type || "regular"}`
              : "One-off cart item"}
            {" • "}Qty: {item.quantity}
          </div>
          <div className="mt-1 text-xs text-gray-400">
            ₹{item.price} each • Total ₹{item.total_price}
            {item.status ? ` • ${item.status}` : ""}
          </div>
        </div>

        <button
          type="button"
          disabled={!deleteId || deletingKey === deleteKey}
          onClick={() => {
            if (!deleteId) return;

            deleteCartEntry({
              kind: isCartItem ? "cart" : "delivery",
              id: deleteId,
              label: item.product_name,
            });
          }}
          className="rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-100 disabled:opacity-40"
        >
          {deletingKey === deleteKey
            ? "Removing..."
            : isCartItem
            ? "Remove Item"
            : "Remove Delivery"}
        </button>
      </div>
    );
  };

  return (
    <>
      <Navbar />

      <main className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-7">
            <h1 className="text-3xl font-bold text-gray-900">
              Update User Cart Item
            </h1>
            <p className="mt-1 text-gray-500">
              View and manage carts, scheduled deliveries, and subscriptions.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
            <section className="h-fit rounded-2xl border bg-white p-4 shadow-sm lg:sticky lg:top-24">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-xl font-semibold text-gray-900">Users</h2>
                <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                  {userTotal}
                </span>
              </div>

              <form onSubmit={handleSearch} className="mb-4 flex gap-2">
                <input
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Search user..."
                  className="min-w-0 flex-1 rounded-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-violet-500"
                />
                <button
                  type="submit"
                  className="rounded-lg bg-violet-600 px-3 py-2 font-semibold text-white"
                >
                  Search
                </button>
              </form>

              {search && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="mb-3 text-sm font-semibold text-violet-700"
                >
                  Clear search
                </button>
              )}

              {usersError && (
                <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {usersError}
                </div>
              )}

              <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                {usersLoading ? (
                  <div className="py-10 text-center text-gray-500">
                    Loading users...
                  </div>
                ) : users.length === 0 ? (
                  <div className="py-10 text-center text-gray-500">
                    No users found.
                  </div>
                ) : (
                  users.map((user) => (
                    <button
                      key={user._id}
                      type="button"
                      onClick={() => fetchUserCart(user)}
                      className={`w-full rounded-xl border p-3 text-left transition ${
                        selectedUser?._id === user._id
                          ? "border-violet-600 bg-violet-50"
                          : "border-gray-200 hover:border-violet-300"
                      }`}
                    >
                      <div className="font-semibold text-gray-900">
                        {user.full_name || "Unnamed user"}
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        {user.phone_number || user.email || user._id}
                      </div>
                      <div className="mt-1 text-xs text-emerald-700">
                        Wallet: ₹{Number(user.wallet_balance || 0).toFixed(2)}
                      </div>
                    </button>
                  ))
                )}
              </div>

              <div className="mt-4 flex items-center justify-between gap-2">
                <button
                  type="button"
                  disabled={userPage === 1 || usersLoading}
                  onClick={() => setUserPage((current) => current - 1)}
                  className="rounded-lg border px-3 py-2 text-sm disabled:opacity-40"
                >
                  ←
                </button>
                <span className="text-sm text-gray-500">
                  Page {userPage} of {userTotalPages}
                </span>
                <button
                  type="button"
                  disabled={userPage >= userTotalPages || usersLoading}
                  onClick={() => setUserPage((current) => current + 1)}
                  className="rounded-lg border px-3 py-2 text-sm disabled:opacity-40"
                >
                  →
                </button>
              </div>
            </section>

            <section className="min-w-0">
              {!selectedUser ? (
                <div className="rounded-2xl border bg-white py-24 text-center text-gray-500 shadow-sm">
                  Select a user to view their cart.
                </div>
              ) : cartLoading ? (
                <div className="rounded-2xl border bg-white py-24 text-center text-gray-500 shadow-sm">
                  Loading user cart...
                </div>
              ) : cartError && !cart ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
                  {cartError}
                  <button
                    type="button"
                    onClick={refreshCart}
                    className="ml-3 font-semibold underline"
                  >
                    Retry
                  </button>
                </div>
              ) : cart ? (
                <div className="space-y-6">
                  <div className="rounded-2xl border bg-white p-5 shadow-sm">
                    <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">
                          {cart.user.full_name || selectedUser.full_name}
                        </h2>
                        <p className="mt-1 text-gray-500">
                          {cart.user.phone_number || selectedUser.phone_number}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <span className="rounded-full bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700">
                          Wallet ₹{Number(cart.user.wallet_balance || 0).toFixed(2)}
                        </span>
                        <span className="rounded-full bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-700">
                          {cart.user.has_membership ? "Member" : "No Membership"}
                        </span>
                      </div>
                    </div>

                    {cartError && (
                      <div className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">
                        {cartError}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border bg-white p-5 shadow-sm">
                    <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          Tomorrow — {cart.tomorrow.delivery_date}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Subtotal ₹{cart.tomorrow.subtotal || 0}
                          {" • "}Total ₹
                          {cart.tomorrow.total_amount ?? cart.tomorrow.subtotal ?? 0}
                        </p>
                      </div>
                      {cart.tomorrow.is_cutoff_passed && (
                        <span className="rounded-full bg-amber-50 px-3 py-1 text-sm font-semibold text-amber-700">
                          Cut-off passed
                        </span>
                      )}
                    </div>

                    <div className="space-y-3">
                      {cart.tomorrow.items?.length ? (
                        cart.tomorrow.items.map((item) =>
                          renderItem(item, cart.tomorrow.delivery_date)
                        )
                      ) : (
                        <p className="py-6 text-center text-gray-500">
                          No items for tomorrow.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border bg-white p-5 shadow-sm">
                    <h3 className="mb-4 text-xl font-bold text-gray-900">
                      Rest of Month
                    </h3>

                    <div className="space-y-5">
                      {cart.rest_of_month?.length ? (
                        cart.rest_of_month.map((group) => (
                          <div key={group.delivery_date}>
                            <div className="mb-2 flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                              <strong>{group.delivery_date}</strong>
                              <span className="text-sm text-gray-500">
                                {group.orders_count ?? group.items.length} items • ₹
                                {group.subtotal || 0}
                              </span>
                            </div>
                            <div className="space-y-2">
                              {group.items.map((item) =>
                                renderItem(item, group.delivery_date)
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="py-6 text-center text-gray-500">
                          No remaining deliveries this month.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border bg-white p-5 shadow-sm">
                    <h3 className="mb-4 text-xl font-bold text-gray-900">
                      Active Subscriptions
                    </h3>

                    <div className="space-y-3">
                      {cart.subscriptions?.length ? (
                        cart.subscriptions.map((subscription) => {
                          const key = `subscription:${subscription.subscription_id}`;

                          return (
                            <div
                              key={subscription.subscription_id}
                              className="flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center"
                            >
                              <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                                {subscription.image ? (
                                  <img
                                    src={subscription.image}
                                    alt={subscription.product_name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full items-center justify-center text-2xl">
                                    🌸
                                  </div>
                                )}
                              </div>

                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-gray-900">
                                  {subscription.product_name}
                                </div>
                                <div className="mt-1 text-sm text-gray-500">
                                  {subscription.sub_type} • Qty: {subscription.quantity}
                                  {" • "}₹{subscription.price}
                                </div>
                                <div className="mt-1 text-xs text-gray-400">
                                  {subscription.remaining_deliveries_count} deliveries remaining
                                </div>
                              </div>

                              <button
                                type="button"
                                disabled={deletingKey === key}
                                onClick={() =>
                                  deleteCartEntry({
                                    kind: "subscription",
                                    id: subscription.subscription_id,
                                    label: subscription.product_name,
                                  })
                                }
                                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-40"
                              >
                                {deletingKey === key
                                  ? "Cancelling..."
                                  : "Cancel Subscription"}
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <p className="py-6 text-center text-gray-500">
                          No active subscriptions.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : null}
            </section>
          </div>
        </div>
      </main>
    </>
  );
}
